/* eslint-disable prefer-destructuring */
import React from "react"
import { useSelector } from "react-redux"

import Images from "assets/images"
import { PROVEN_NO, PROVEN_IN_USE, PRIOR_USE, PUBLISHED } from "const"
import {
    SilDeviceNested,
    SilProjectDeviceNested,
    SilSubsystemBlock,
    MtProject,
    QidProduct,
    QidModel,
    QidModelSafetyFunction,
    QidModelOption,
    QidDatapoint,
} from "server/models"
import { QidCalculationMethod, SilType, SilMode, ProvenType } from "client/utils"
import {
    StoreType,
    QidResultType,
    ModelTargetType,
    SilModelDeviceOnSelect,
} from "client/store"
import {
    Logo,
    TableColumn,
    TablePopoverData,
    TablePopover,
} from "client/components"

import FormatScientific from "client/pages/sil/format/scientific"
import { getSilMode } from "client/pages/sil/formulas"

type CommonType = QidProduct | QidModel | QidModelSafetyFunction

export type ColumnsType = ReturnType<typeof Columns>

export interface SelectDeviceType {
    product?: QidProduct
    model?: QidModel
    safetyFunction?: QidModelSafetyFunction
    option?: QidModelOption
    datapoint?: QidDatapoint
    dataAvg: QidResultType
    method: QidCalculationMethod

    project: MtProject
    modelTarget: ModelTargetType
    onSelect: SilModelDeviceOnSelect
}

export const SelectDevice = ({
    product = null,
    model = null,
    safetyFunction = null,
    option = null,
    datapoint = null,
    dataAvg,
    method,

    project,
    modelTarget,
    onSelect,
}: SelectDeviceType) => {
    let type: SilType
    let mode: SilMode
    let lowDemandMode: boolean
    let highDemandMode: boolean
    let continuousMode: boolean
    let logicSolver: boolean
    let provenInUse: ProvenType

    // get data from the lowest level
    if (safetyFunction) {
        type = safetyFunction.type
        mode = getSilMode(safetyFunction)
        lowDemandMode = safetyFunction.lowDemandMode
        highDemandMode = safetyFunction.highDemandMode
        continuousMode = safetyFunction.continuousMode
        logicSolver = safetyFunction.logicSolver
        provenInUse = safetyFunction.provenInUse
    } else if (model) {
        type = model.type
        mode = getSilMode(model)
        lowDemandMode = model.lowDemandMode
        highDemandMode = model.highDemandMode
        continuousMode = model.continuousMode
        logicSolver = model.logicSolver
        provenInUse = model.provenInUse
    } else if (product) {
        type = product.type
        mode = getSilMode(product)
        lowDemandMode = product.lowDemandMode
        highDemandMode = product.highDemandMode
        continuousMode = product.continuousMode
        logicSolver = product.logicSolver
        provenInUse = product.provenInUse
    }

    const qidProductId = product ? product.id : null
    const qidModelId = model ? model.id : null
    const qidSafetyFunctionId = safetyFunction ? safetyFunction.id : null
    const qidOptionId = option ? option.id : null
    const qidDatapointId = datapoint ? datapoint.id : null

    const qidModelName = model ? model.name : null
    const qidSafetyFunctionName = safetyFunction ? safetyFunction.name : null
    const qidOptionName = option ? option.name : null
    const qidDatapointDate = datapoint ? datapoint.publicationDate : null
    const qidMethod = method

    const manufacturer = product && product.company ? product.company.name : null
    const name = product ? product.name : null
    const logoId = product ? product.logoId : null
    const logo = product ? product.logo : null

    const sd = dataAvg.sd === null ? null : dataAvg.sd.toString()
    const su = dataAvg.su === null ? null : dataAvg.su.toString()
    const dd = dataAvg.dd === null ? null : dataAvg.dd.toString()
    const du = dataAvg.du === null ? null : dataAvg.du.toString()

    if (modelTarget === "subsystem") {
        const nestedDevice: SilDeviceNested = {
            id: null,
            parentDeviceId: null,
            deviceId: null,

            qidProductId,
            qidModelId,
            qidSafetyFunctionId,
            qidOptionId,
            qidDatapointId,

            qidModelName,
            qidSafetyFunctionName,
            qidOptionName,
            qidDatapointDate,
            qidMethod,

            manufacturer,
            name,
            logoId,
            logo,
            type,
            mode,
            logicSolver,
            provenInUse,

            sd,
            su,
            dd,
            du,

            order: null,
        }

        const nestedProjectDevice: SilProjectDeviceNested = {
            ...nestedDevice,
            projectDeviceId: null,
        }

        onSelect(project.id ? nestedProjectDevice : nestedDevice)
    }

    if (modelTarget === "safetyFunction") {
        const subsystemBlock: SilSubsystemBlock = {
            id: null,
            subsystemId: null,
            deviceId: null,
            projectDeviceId: null,

            qidProductId,
            qidModelId,
            qidSafetyFunctionId,
            qidOptionId,
            qidDatapointId,
            qidMethod,

            order: null,
            projectDevice: {
                id: null,
                projectId: project.id,
                deviceId: null,

                qidProductId,
                qidModelId,
                qidSafetyFunctionId,
                qidOptionId,
                qidDatapointId,

                qidModelName,
                qidSafetyFunctionName,
                qidOptionName,
                qidDatapointDate,
                qidMethod,

                manufacturer,
                name,
                logoId,
                logo,
                description: "",
                reference: "",
                nested: false,
                type,
                lowDemandMode,
                highDemandMode,
                continuousMode,
                logicSolver,
                provenInUse,

                failureRate: true,
                sff: null,
                dc: null,
                pfd: null,
                pfh: null,
                sil: null,

                sd,
                su,
                dd,
                du,

                status: PUBLISHED,
                copy: 0,
                updated: false,

                nestedDevices: [],
            },
        }

        onSelect(subsystemBlock)
    }
}

const Columns = () => {
    const lang = useSelector((state: StoreType) => state.system.lang)

    const productColumns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
            cellMinWidth: "80px",
        },
        {
            field: "logo",
            header: lang.logo,
            centerAlign: true,
            cellWidth: "64px",
            cellMinWidth: "64px",
            formatter: (product: QidProduct) => (
                <Logo table logo={product.logo || Images.qidProductLogo} />
            ),
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "200px",
            cellMaxWidth: "300px",
        },
        {
            field: "company",
            header: lang.company,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "250px",
            formatter: (product: QidProduct) => product.company.name,
        },
    ]

    const commonColumns: TableColumn[] = [
        {
            field: "type",
            header: lang.type,
            sortable: true,
            cellMinWidth: "100px",
        },
        {
            field: "mode",
            header: lang.modes,
            cellMinWidth: "150px",
            formatter: (row: CommonType) => {
                const dataList: TablePopoverData[] = []

                if (row.lowDemandMode) {
                    dataList.push({
                        id: `${row.id}_${row.name}_lowDemandMode`,
                        title: lang.modeLowDemand,
                    })
                }
                if (row.highDemandMode) {
                    dataList.push({
                        id: `${row.id}_${row.name}_highDemandMode`,
                        title: lang.modeHighDemand,
                    })
                }
                if (row.continuousMode) {
                    dataList.push({
                        id: `${row.id}_${row.name}_continuousMode`,
                        title: lang.modeContinuous,
                    })
                }

                return (
                    <TablePopover
                        id={`${row.id}_${row.name}_modeList`}
                        dataList={dataList}
                        placement="right"
                        title={lang.modes}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "logicSolver",
            header: lang.logicSolver,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (row: CommonType) => (row.logicSolver ? lang.yes : lang.no),
        },
        {
            field: "provenInUse",
            header: lang.provenReliabilityData,
            sortable: true,
            cellMinWidth: "200px",
            formatter: (row: CommonType) => {
                if (row.provenInUse === PROVEN_NO) return lang.no
                if (row.provenInUse === PROVEN_IN_USE) return lang.provenInUse
                if (row.provenInUse === PRIOR_USE) return lang.priorUse
                return null
            },
        },
    ]

    const resultColumns: TableColumn[] = [
        {
            field: "sd",
            header: "SD",
            cellMinWidth: "150px",
            formatter: (data: QidResultType) => <FormatScientific data={data.sd} tableView />,
        },
        {
            field: "su",
            header: "SU",
            cellMinWidth: "150px",
            formatter: (data: QidResultType) => <FormatScientific data={data.su} tableView />,
        },
        {
            field: "dd",
            header: "DD",
            cellMinWidth: "150px",
            formatter: (data: QidResultType) => <FormatScientific data={data.dd} tableView />,
        },
        {
            field: "du",
            header: "DU",
            cellMinWidth: "150px",
            formatter: (data: QidResultType) => <FormatScientific data={data.du} tableView />,
        },
    ]

    return {
        productColumns,
        commonColumns,
        resultColumns,
    }
}
export default Columns
